import { ModuleName } from '@capital-access/common/utils';
import { ContactInformationResponse } from '@capital-access/profiles/common';
import {
  AdditionalConfiguration,
  EXPORT_REPORT_MODULE_NAME,
  ReportSection,
  ReportSectionConfigurationBase,
  ReportType,
  ReportViewModelSection
} from '@capital-access/reporting/common';

export class ContactBiographyConfiguration extends ReportSectionConfigurationBase {
  typeName = 'ContactBiographyConfiguration';
  readonly applicableReportTypes = [ReportType.Contact, ReportType.Institution];
  minSize = 2;
  maxSize = 4;
  defaultSize = 4;
  exportReportModuleName = localStorage.getItem(EXPORT_REPORT_MODULE_NAME) as ModuleName;
  override additionalConfiguration: AdditionalConfiguration = {
    isTruncated: false,
    truncationLength: 5
  };

  get availableDataSections() {
    const sections = [];
    if (this.appliedReportType === ReportType.Institution) {
      sections.push({
        name: 'contactBiographyNameJob',
        displayName: 'Name, Job Role',
        isSelected: true,
        canConfigure: false,
        isStatic: true
      });
    }

    sections.push(
      {
        name: 'contactBiographyBio',
        displayName: 'Biography',
        isSelected: true,
        canConfigure: true
      },
      {
        name: 'contactBiographyScoutingReport',
        displayName: 'Scouting Report',
        isSelected: false,
        canConfigure: true
      },
      {
        name: 'contactBiographyEducation',
        displayName: 'Education',
        isSelected: false,
        canConfigure: true
      }
    );

    return sections;
  }

  override readonly configurationControls = [
    {
      name: 'contactBiographyBioTruncation',
      displayName: 'Biography Truncation',
      applicableDataSection: 'contactBiographyBio',
      type: 'truncation',
      defaultLength: 700
    },
    {
      name: 'contactBiographyScoutingReportTruncation',
      displayName: 'Scouting Report Truncation',
      applicableDataSection: 'contactBiographyScoutingReport',
      type: 'truncation',
      defaultLength: 700
    }
  ];

  get reportSection(): ReportSection {
    if (this.appliedReportType === ReportType.Contact) {
      return ReportSection.ContactBiography;
    } else if (this.appliedReportType === ReportType.Institution) {
      return ReportSection.PrimaryContactBiography;
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get name(): string {
    if (this.appliedReportType === ReportType.Contact || this.appliedReportType === ReportType.Institution) {
      return 'contactBiography';
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get nodataLocalizationKeyName(): string {
    if (this.appliedReportType === ReportType.Contact) {
      return 'contactBiography';
    } else if (
      this.appliedReportType === ReportType.Institution &&
      this.exportReportModuleName === ModuleName.EngageAttendees
    ) {
      return 'participantBiography';
    } else if (this.appliedReportType === ReportType.Institution) {
      return 'primaryContactBiography';
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get displayName(): string {
    if (this.appliedReportType === ReportType.Contact) {
      return 'Contact Biography';
    } else if (this.appliedReportType === ReportType.Institution) {
      return 'Primary Contact Biography';
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get reportViewModelSections(): ReportViewModelSection[] {
    if (this.appliedReportType === ReportType.Contact) {
      return [ReportViewModelSection.InvestorProfile_ContactBiography];
    } else if (this.appliedReportType === ReportType.Institution) {
      return [ReportViewModelSection.InvestorProfile_PrimaryContactBiography];
    } else {
      throw new Error('Report Type not supported');
    }
  }
}

export interface BiographyModel {
  name: string;
  jobTitle: string;
  biography: string;
  scoutingReport: string;
  education: string;
}

export interface ParticipantBiographyModel {
  contacts: ContactInformationResponse[];
}

export interface BiographyData {
  [ReportViewModelSection.InvestorProfile_ContactBiography]: BiographyModel;
  [ReportViewModelSection.InvestorProfile_PrimaryContactBiography]: BiographyModel | ParticipantBiographyModel;
}
